import { openai } from '../../config/openai';
import { Department } from '../../types';
import { ExcelTaskData, SubTaskFormData } from '../../types/task';
import { useStore } from '../../store/useStore';
import { logger } from '../../utils/logger';

interface CustomerData {
  customerName: string;
  date: string;
  dueDate: string;
  over45Days: number;
  invoices: {
    number: string;
    date: string;
    dueDate: string;
    amount: number;
  }[];
}

export interface ProcessedCustomer {
  customerName: string;
  date: string;
  dueDate: string;
  over45Days: number;
  subtasks: SubTaskFormData[];
}

export const processExcelData = async (content: string, department: Department): Promise<ProcessedCustomer[]> => {
  try {
    logger.info(`Processing Excel data for ${department}`);

    // Split content into lines and clean up
    const lines = content
      .split('\n')
      .map(line => line.trim())
      .filter(line => line);

    if (lines.length === 0) {
      throw new Error('No data found in file');
    }

    // Track customers with their invoices
    const customerMap = new Map<string, CustomerData>();
    let currentCustomer: string | null = null;

    // Process each line
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const columns = line.split(',').map(col => col.trim());

      // Skip header row
      if (i === 0 || columns.length < 4) continue;

      // Check if this is a customer row (non-numeric first column)
      if (isNaN(Number(columns[0]))) {
        currentCustomer = columns[0].trim();
        
        // Initialize customer data
        if (!customerMap.has(currentCustomer)) {
          customerMap.set(currentCustomer, {
            customerName: currentCustomer,
            date: formatDate(new Date().toLocaleDateString()),
            dueDate: formatDate(new Date().toLocaleDateString()),
            over45Days: 0,
            invoices: []
          });
        }

        // Get the over45Days amount from the correct column (index 6 in your example)
        const over45DaysStr = columns[6]?.replace(/[$,]/g, '') || '0';
        const over45Days = parseFloat(over45DaysStr) || 0;

        if (over45Days > 0) {
          const customer = customerMap.get(currentCustomer)!;
          customer.over45Days = over45Days;
          customerMap.set(currentCustomer, customer);
        }
      } else if (currentCustomer && customerMap.has(currentCustomer)) {
        // This is an invoice row
        const customer = customerMap.get(currentCustomer)!;
        
        // Parse invoice details
        const invoiceNumber = columns[0];
        const invoiceDate = formatDate(columns[1]);
        const invoiceDueDate = formatDate(columns[2]);
        const amountStr = columns[6]?.replace(/[$,]/g, '') || '0';
        const amount = parseFloat(amountStr) || 0;

        if (amount > 0) {
          customer.invoices.push({
            number: invoiceNumber,
            date: invoiceDate,
            dueDate: invoiceDueDate,
            amount
          });
        }
      }
    }

    // Convert customer map to ProcessedCustomer array
    const customers: ProcessedCustomer[] = [];
    for (const [customerName, data] of customerMap) {
      if (data.over45Days > 0) {
        const subtasks: SubTaskFormData[] = data.invoices
          .filter(invoice => invoice.amount > 0)
          .map(invoice => ({
            title: `Process Invoice #${invoice.number}`,
            description: `Amount: $${invoice.amount.toFixed(2)}\nDue Date: ${invoice.dueDate}`,
            dueDate: invoice.dueDate,
            dueTime: '17:00',
            hasDueDate: true
          }));

        if (subtasks.length > 0) {
          customers.push({
            customerName,
            date: data.date,
            dueDate: data.dueDate,
            over45Days: data.over45Days,
            subtasks
          });
        }
      }
    }

    // Sort by amount descending
    customers.sort((a, b) => b.over45Days - a.over45Days);

    if (customers.length === 0) {
      throw new Error('No valid customer data found with amounts over 45 days');
    }

    logger.info('Successfully processed Excel data:', {
      customerCount: customers.length,
      totalSubtasks: customers.reduce((sum, c) => sum + c.subtasks.length, 0),
      totalAmount: customers.reduce((sum, c) => sum + c.over45Days, 0).toFixed(2)
    });

    return customers;
  } catch (error) {
    logger.error('Excel processing error:', error);
    throw error;
  }
};

const formatDate = (dateStr: string): string => {
  if (!dateStr) return new Date().toISOString().split('T')[0];
  
  try {
    // Handle different date formats
    let date: Date;
    
    if (dateStr.includes('/')) {
      // MM/DD/YYYY format
      const [month, day, year] = dateStr.split('/');
      date = new Date(Number(year), Number(month) - 1, Number(day));
    } else if (dateStr.includes('-')) {
      // YYYY-MM-DD format
      date = new Date(dateStr);
    } else {
      // Invalid format, use current date
      date = new Date();
    }

    // Validate date
    if (isNaN(date.getTime())) {
      return new Date().toISOString().split('T')[0];
    }

    // Format as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  } catch (error) {
    logger.error('Date formatting error:', error);
    return new Date().toISOString().split('T')[0];
  }
};

export const suggestTaskPriority = async (description: string): Promise<string> => {
  try {
    const response = await openai.chat.completions.create({
      model: "gpt-3.5-turbo",
      messages: [{
        role: "system",
        content: `Analyze A/R aging data and suggest priority level. Rules:
          - URGENT: Any amount over 45 days past due
          - MEDIUM: No amounts over 45 days past due`
      }, {
        role: "user",
        content: description
      }],
      temperature: 0.1,
    });

    const priority = response.choices[0].message.content.toUpperCase();
    return priority === 'URGENT' ? 'URGENT' : 'MEDIUM';
  } catch (error) {
    logger.error('Priority suggestion error:', error);
    return 'MEDIUM';
  }
};